import { DocumentFileList } from "@/common/components/DocumentFileList"
import { MultipleDocumentSoftUpload } from "@/common/components/MultipleDocumentSoftUpload"
import useBlobs from "@/common/hooks/queries/useBlobs"
import { ErrorsMapper } from "@/common/services/errorsMapper"
import { getFilenameFromUrl } from "@/common/utils/getFilenameFromUrl"
import { useCreateEstablishmentLeadGenericDocument } from "@/features/establishmentLeadsFunnel/hooks/useCreateEstablishmentLeadGenericDocument"
import { useDeleteEstablishmentLeadGenericDocument } from "@/features/establishmentLeadsFunnel/hooks/useDeleteEstablishmentLeadGenericDocument"
import { useEstablishmentLead } from "@/features/establishmentLeadsFunnel/hooks/useEstablishmentLead"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Control } from "react-hook-form"

interface GenericDocumentsSectionProps {
  establishmentLeadId?: string
  generic_documents: EstablishmentLeadData['generic_documents']
  control: Control<EstablishmentLeadsFunnelUpdateValidationSchema, any>
  disabled?: boolean
}

export const GenericDocumentsSection = ({ establishmentLeadId, generic_documents, control, disabled }: GenericDocumentsSectionProps) => {
  const queryClient = useQueryClient()
  const [serverError, setServerError] = useState('')

  const { isFetching } = useEstablishmentLead(establishmentLeadId || '')

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs({
    onSuccess: (response) => {
      createEstablishmentLeadGenericDocument({ blob_id: response.data.data.id })
    },
  })

  const { mutate: createEstablishmentLeadGenericDocument, isPending: createEstablishmentLeadIsPending } = useCreateEstablishmentLeadGenericDocument({
    establishmentLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })
      setServerError('')
    },
    onError: (error) => {
      const transformedErrors = ErrorsMapper.default()(error?.response?.data.errors)

      !!transformedErrors.length && setServerError(transformedErrors.map((error) => error.generic_documents).join(', '))
    }
  })

  const { mutate: deleteEstablishmentLeadGenericDocument, isPending: deleteEstablishmentLeadIsPending } = useDeleteEstablishmentLeadGenericDocument({
    establishmentLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLeadId] })
    },
  })

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[18px] font-bold">Anexos</p>
      {serverError && <p className="text-sm text-danger-soft">{serverError}</p>}
      {disabled ? (
        <DocumentFileList
          files={generic_documents?.map((document) => ({
            id: document.attachment_id,
            fileName: getFilenameFromUrl(document?.url),
            currentFileUrl: document?.url,
          })) || []}
        />
      ) : (
        <MultipleDocumentSoftUpload
          title="Anexe um arquivo"
          name="generic_documents"
          files={generic_documents}
          accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
          uploadBlob={uploadBlob}
          deletePrivateCompanyLeadGenericDocument={deleteEstablishmentLeadGenericDocument}
          control={control}
          fieldName="generic_documents"
          disabled={uploadBlobIsPending || createEstablishmentLeadIsPending || deleteEstablishmentLeadIsPending || isFetching}
        />
      )}
    </div>
  )
}
